import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Box, Star, Layers } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import BoardPatternFormTabs from './BoardPatternFormTabs';
import ConfirmModal from './ConfirmModal';

const BoardPatternsManager = () => {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    loadPatterns();
  }, []);

  const loadPatterns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/board-patterns');
      if (response.data.success) {
        setPatterns(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar padrões:', error);
      toast.error('Erro ao carregar padrões');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingPattern(null);
    setShowForm(true);
  };

  const handleEdit = (pattern) => {
    setEditingPattern(pattern);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmAction({ id, action: 'delete' });
  };

  const confirmDelete = async () => {
    if (!confirmAction) return;
    try {
      await api.delete(`/board-patterns/${confirmAction.id}`);
      toast.success('Padrão deletado com sucesso');
      loadPatterns();
    } catch (error) {
      console.error('Erro ao deletar padrão:', error);
      toast.error('Erro ao deletar padrão');
    } finally {
      setConfirmAction(null);
    }
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingPattern(null);
    loadPatterns();
  };

  const filteredPatterns = patterns.filter(p => {
    const matchesSearch = !searchTerm ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || p.material_type === filterType;
    return matchesSearch && matchesType;
  });

  const materialTypes = [...new Set(patterns.map(p => p.material_type).filter(Boolean))];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-blue-600" />
            Padrões de Chapas
          </h1>
          <p className="text-gray-600">Gerencie padrões de chapas para corte</p>
        </div>
        <button
          onClick={handleNew}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Novo Padrão
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por descrição, referência ou fabricante..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos os materiais</option>
          {materialTypes.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* Listagem */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Carregando padrões...</p>
        </div>
      ) : filteredPatterns.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Box className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">
            {searchTerm || filterType ? 'Nenhum padrão encontrado' : 'Nenhum padrão cadastrado'}
          </p>
          <button
            onClick={handleNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Criar primeiro padrão
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Padrão</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Esp.</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estoque</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cor</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPatterns.map((pattern) => (
                  <tr key={pattern.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {pattern.is_favorite && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                        <div>
                          <p className="font-medium text-gray-900">{pattern.description}</p>
                          {pattern.reference && (
                            <p className="text-xs text-gray-500">Ref: {pattern.reference}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm">{pattern.material_type}</span>
                      {pattern.thickness && (
                        <p className="text-xs text-gray-500">{pattern.manufacturer || '—'}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium">{pattern.thickness}mm</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm">
                        {pattern.total_sheets_available || 0} chapas
                      </span>
                      {parseInt(pattern.enabled_sheets_count) > 0 && (
                        <p className="text-xs text-green-600">{pattern.enabled_sheets_count} habilitadas</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {pattern.color_hex && (
                          <span
                            className="w-5 h-5 rounded border border-gray-300"
                            style={{ backgroundColor: pattern.color_hex }}
                          />
                        )}
                        <span className="text-sm text-gray-600">
                          {pattern.color || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        pattern.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {pattern.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(pattern)}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm flex items-center gap-1"
                          title="Editar"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(pattern.id)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm flex items-center gap-1"
                          title="Excluir"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de formulário */}
      {showForm && (
        <BoardPatternFormTabs
          pattern={editingPattern}
          onClose={() => {
            setShowForm(false);
            setEditingPattern(null);
          }}
          onSuccess={handleSuccess}
        />
      )}

      {confirmAction?.action === 'delete' && (
        <ConfirmModal
          title="Confirmar exclusão"
          message="Tem certeza que deseja deletar este padrão? Chapas e retalhos associados também serão removidos."
          onConfirm={confirmDelete}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

export default BoardPatternsManager;